import { useState, useEffect, useRef } from 'react'
import { Info } from 'lucide-react'
import type { SyncStatus } from '../db/sync'
import { Tooltip } from './ui/Tooltip'

interface StatusBarProps {
  noteCount: number
  wordCount: number
  saveStatus: 'idle' | 'saving' | 'saved'
  syncStatus: SyncStatus
  isAuthenticated: boolean
}

const shortcuts: { syntax: string; desc: string }[][] = [
  [
    { syntax: '**text**', desc: 'Bold' },
    { syntax: '*text*', desc: 'Italic' },
    { syntax: '~~text~~', desc: 'Strikethrough' },
    { syntax: '`code`', desc: 'Inline code' },
    { syntax: '==text==', desc: 'Highlight' },
    { syntax: '> ', desc: 'Quote' },
    { syntax: '---', desc: 'Divider' },
  ],
  [
    { syntax: '# ## ###', desc: 'Headings' },
    { syntax: '- ', desc: 'Bullet list' },
    { syntax: '1. ', desc: 'Numbered list' },
    { syntax: '- [ ] ', desc: 'Checkbox' },
    { syntax: '```', desc: 'Code block' },
    { syntax: '/', desc: 'Commands' },
  ],
]

export function StatusBar({ noteCount, wordCount, saveStatus, syncStatus, isAuthenticated }: StatusBarProps) {
  const [showShortcuts, setShowShortcuts] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!showShortcuts) return
    const handler = (e: MouseEvent) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setShowShortcuts(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showShortcuts])

  const getSaveLabel = () => {
    if (isAuthenticated) {
      if (syncStatus === 'syncing') return 'Syncing to cloud…'
      if (syncStatus === 'error') return 'Sync failed'
      if (syncStatus === 'unsynced') return 'Saved locally'
      return 'Synced to cloud ✓'
    }
    if (saveStatus === 'saving') return 'Saving…'
    if (saveStatus === 'saved') return 'Saved locally'
    return null
  }

  const saveLabel = getSaveLabel()

  return (
    <footer 
      className="h-7 sm:h-8 flex items-center justify-between px-3 sm:px-5 text-[10px] sm:text-xs border-t relative"
      style={{ 
        backgroundColor: 'hsl(var(--color-bg-secondary))',
        borderColor: 'hsl(var(--color-border))',
        color: 'hsl(var(--color-text-muted))'
      }}
    >
      <span>{noteCount} {noteCount === 1 ? 'note' : 'notes'}</span>

      {/* Center: sync dot + sync status + shortcuts toggle */}
      <div className="flex items-center gap-3">
        {/* Sync colour dot — only for logged-in users */}
        {isAuthenticated && (
          <span
            className={`inline-block w-2 h-2 rounded-full flex-shrink-0${
              syncStatus === 'syncing' ? ' animate-pulse' : ''
            }`}
            style={{
              backgroundColor:
                syncStatus === 'syncing'
                  ? 'hsl(142 70% 45%)'
                  : syncStatus === 'idle' || syncStatus === 'success'
                    ? 'hsl(142 70% 45%)'
                    : 'hsl(38 90% 50%)',
            }}
            title={
              syncStatus === 'syncing'
                ? 'Syncing…'
                : syncStatus === 'idle' || syncStatus === 'success'
                  ? 'Synced'
                  : 'Not synced'
            }
          />
        )}
        {saveLabel && (
          <span
            className="transition-opacity duration-300"
            style={{
              color: syncStatus === 'error' && isAuthenticated
                ? 'hsl(var(--color-error))'
                : 'hsl(var(--color-text-muted))',
            }}
          >
            {saveLabel}
          </span>
        )}
        <span style={{ color: 'hsl(var(--color-border))' }}>·</span>
        <div className="flex items-center gap-1.5">
          <button
            ref={btnRef}
            onClick={() => setShowShortcuts(prev => !prev)}
            className="flex items-center gap-1 rounded transition-fast hover:opacity-70"
            style={{ color: showShortcuts ? 'hsl(var(--color-accent))' : 'hsl(var(--color-text-muted))' }}
          >
            <span className="font-mono text-[10px] font-bold">#</span>
            <span className="hidden sm:inline">Markdown</span>
          </button>
          <Tooltip content="Click to view all shortcuts" side="top">
            <Info 
              size={14} 
              className="cursor-pointer hover:opacity-100 transition-opacity" 
              style={{ color: 'hsl(var(--color-text-muted))', opacity: 0.7 }} 
              onClick={() => setShowShortcuts(prev => !prev)}
            />
          </Tooltip>
        </div>
      </div>

      {/* Markdown shortcuts popup */}
      {showShortcuts && (
        <div
          ref={popupRef}
          className="md-shortcuts-popup fixed z-50"
          style={{
            bottom: 'calc(2rem + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <div className="md-shortcuts-title">Markdown Cheat Sheet</div>
          <div className="md-shortcuts-columns">
            {shortcuts.map((col, ci) => (
              <div key={ci} className="md-shortcuts-col">
                {col.map(s => (
                  <div key={s.syntax} className="md-shortcuts-item">
                    <code>{s.syntax}</code>
                    <span>{s.desc}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="md-shortcuts-tip">Type at the start of a line or wrap text</div>
        </div>
      )}

      <span>{wordCount} {wordCount === 1 ? 'word' : 'words'}</span>
    </footer>
  )
}
